import React from 'react';

interface SparklineProps {
  data: number[];
  color?: string;
  filled?: boolean;
  width?: number;
  height?: number;
  strokeWidth?: number;
  ariaLabel?: string;
  className?: string;
}

/**
 * Mini gráfico de tendência em SVG puro.
 * Normaliza os valores entre min/max e desenha polyline + área opcional.
 * O círculo final destaca o último ponto.
 */
export function Sparkline({
  data,
  color = 'currentColor',
  filled = false,
  width = 96,
  height = 42,
  strokeWidth = 1.6,
  ariaLabel,
  className = '',
}: SparklineProps) {
  if (!data || data.length < 2) {
    return <div aria-hidden className={className} style={{ width, height }} />;
  }

  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const pad = strokeWidth + 2;

  const pts = data.map((v, i) => {
    const x = i * step;
    const y = height - pad - ((v - min) / range) * (height - pad * 2);
    return [x, y] as const;
  });

  const path = pts
    .map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ',' + p[1].toFixed(1))
    .join(' ');

  const area = `${path} L${width.toFixed(1)},${height.toFixed(1)} L0,${height.toFixed(1)} Z`;
  const [lx, ly] = pts[pts.length - 1];

  return (
    <svg
      role="img"
      aria-label={ariaLabel || `Tendência com ${data.length} pontos`}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      preserveAspectRatio="none"
    >
      {filled && <path d={area} fill={color} opacity={0.12} />}
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lx} cy={ly} r={2.5} fill={color} />
    </svg>
  );
}
